export const Permission = {
  CreateUser: 'create_user',
  ManageUsers: 'manage_users',
  ManageRoles: 'manage_roles',
  ApproveJoinRequests: 'approve_join_requests',
  ManageEvents: 'manage_events',
  EditGuidelines: 'edit_guidelines',
  EditFaq: 'edit_faq',
  EditHomepage: 'edit_homepage',
  EditJoinQuestions: 'edit_join_questions',
  ManageSurveys: 'manage_surveys',
  TagOfficialEvent: 'tag_official_event',
  TagClubEvent: 'tag_club_event',
  ManageDocuments: 'manage_documents',
  ImportPayments: 'import_payments',
} as const;

export type PermissionKey = (typeof Permission)[keyof typeof Permission];

// The built-in admin role is named "admin". When present as default role,
// it grants every permission. Mirrors user.dart:hasPermission.
export const ADMIN_ROLE_NAME = 'admin';

// Admin roll-up: any of these grants access to /admin. Order matches
// user.dart:hasAnyAdminPermission.
const ADMIN_PERMISSIONS: readonly PermissionKey[] = [
  Permission.ManageEvents,
  Permission.ManageUsers,
  Permission.ApproveJoinRequests,
  Permission.EditJoinQuestions,
  Permission.ManageDocuments,
];

export interface UserLike {
  roles: readonly {
    name: string;
    isDefault: boolean;
    permissions: readonly string[];
  }[];
}

// defense-in-depth: backend already sends string[], but guard corrupt/missing values so render can't throw
export function normalizePermissions(value: readonly string[] | null | undefined): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

export function isAdmin(user: UserLike | null): boolean {
  if (!user) return false;
  return user.roles.some((r) => r.name === ADMIN_ROLE_NAME && r.isDefault);
}

export function hasPermission(user: UserLike | null, key: PermissionKey): boolean {
  if (!user) return false;
  return isAdmin(user) || user.roles.some((r) => normalizePermissions(r.permissions).includes(key));
}

export function hasAnyAdminPermission(user: UserLike | null): boolean {
  if (!user) return false;
  return ADMIN_PERMISSIONS.some((p) => hasPermission(user, p));
}
